import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMemberSubscriptions, getCenterSubscriptions, getCenterSubscriptionSummary, createSubscription, updateSubscription, refundSubscription, deleteSubscription, pauseSubscription, resumeSubscription, cancelPause, updatePausePeriod, getPausePeriods, getFacilityTransferPreview, transferFacilitySubscription, getFacilityTransfers } from './api';
import { facilitySubscriptionKeys } from './keys';
import { publicMemberKeys } from '../publicMember/keys';
import { lockerKeys } from '../locker/keys';
import { CreateSubscriptionBody, UpdateSubscriptionBody, SubscriptionStatusFilter, SubscriptionDateBase, PauseSubscriptionBody, ResumeSubscriptionBody, CancelPauseBody, UpdatePausePeriodBody, RefundSubscriptionBody, FacilityTransferPreviewParams, FacilityTransferBody } from './types';

export const useMemberSubscriptions = ({ centerId, memberId, enabled = true }: { centerId: string; memberId: string; enabled?: boolean }) =>
    useQuery({
        queryKey: facilitySubscriptionKeys.memberList(centerId, memberId),
        queryFn: () => getMemberSubscriptions({ centerId, memberId }),
        enabled: enabled && !!centerId && !!memberId,
        select: (data) => {
            if (!data?.data) return data?.data;
            const raw = data.data as any;
            // API 응답: { subscriptions[] } → 프론트: { results[] }
            return {
                ...raw,
                results: raw.results ?? raw.subscriptions ?? [],
            };
        },
    });

export const useCenterSubscriptions = ({
    centerId,
    status,
    category,
    search,
    dateBase,
    dateFrom,
    dateTo,
    page,
    limit,
    enabled = true,
}: {
    centerId: string;
    status?: SubscriptionStatusFilter;
    category?: string;
    search?: string;
    dateBase?: SubscriptionDateBase;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...facilitySubscriptionKeys.list(centerId), status, category, search, dateBase, dateFrom, dateTo, page, limit],
        queryFn: () => getCenterSubscriptions({ centerId, status, category, search, dateBase, dateFrom, dateTo, page, limit }),
        enabled: enabled && !!centerId,
        select: (data) => {
            if (!data?.data) return data?.data;
            const raw = data.data as any;
            // API 응답: { total } → 프론트: { totalResults }
            return {
                ...raw,
                totalResults: raw.totalResults ?? raw.total ?? 0,
            };
        },
    });

export const useCenterSubscriptionSummary = ({
    centerId,
    status,
    category,
    search,
    dateBase,
    dateFrom,
    dateTo,
    enabled = true,
}: {
    centerId: string;
    status?: SubscriptionStatusFilter;
    category?: string;
    search?: string;
    dateBase?: SubscriptionDateBase;
    dateFrom?: string;
    dateTo?: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...facilitySubscriptionKeys.summary(centerId), status, category, search, dateBase, dateFrom, dateTo],
        queryFn: () => getCenterSubscriptionSummary({ centerId, status, category, search, dateBase, dateFrom, dateTo }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data,
    });

export const useCreateSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, memberId, body }: { centerId: string; memberId: string; body: CreateSubscriptionBody }) =>
            createSubscription({ centerId, memberId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: facilitySubscriptionKeys.all });
            // 라커 관리/구역 현황도 함께 invalidate (lockerNumber 변경 → 라커 관리 페이지 동기화)
            queryClient.invalidateQueries({ queryKey: lockerKeys.all });
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(variables.centerId) });
            queryClient.invalidateQueries({
                queryKey: facilitySubscriptionKeys.memberList(variables.centerId, variables.memberId),
            });
            queryClient.invalidateQueries({
                queryKey: publicMemberKeys.detail(variables.centerId, variables.memberId),
            });
        },
    });
};

export const useUpdateSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, subscriptionId, body }: { centerId: string; subscriptionId: string; body: UpdateSubscriptionBody; memberId?: string }) =>
            updateSubscription({ centerId, subscriptionId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: facilitySubscriptionKeys.all });
            // 라커 관리/구역 현황도 함께 invalidate (lockerNumber 변경 → 라커 관리 페이지 동기화)
            queryClient.invalidateQueries({ queryKey: lockerKeys.all });
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(variables.centerId) });
            if (variables.memberId) {
                queryClient.invalidateQueries({
                    queryKey: facilitySubscriptionKeys.memberList(variables.centerId, variables.memberId),
                });
                queryClient.invalidateQueries({
                    queryKey: publicMemberKeys.detail(variables.centerId, variables.memberId),
                });
            }
        },
    });
};

export const useRefundSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            subscriptionId,
            body,
        }: {
            centerId: string;
            subscriptionId: string;
            body: RefundSubscriptionBody;
            memberId?: string;
        }) => refundSubscription({ centerId, subscriptionId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: facilitySubscriptionKeys.all });
            // 라커 관리/구역 현황도 함께 invalidate (lockerNumber 변경 → 라커 관리 페이지 동기화)
            queryClient.invalidateQueries({ queryKey: lockerKeys.all });
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(variables.centerId) });
            if (variables.memberId) {
                queryClient.invalidateQueries({
                    queryKey: facilitySubscriptionKeys.memberList(variables.centerId, variables.memberId),
                });
                queryClient.invalidateQueries({
                    queryKey: publicMemberKeys.detail(variables.centerId, variables.memberId),
                });
            }
        },
    });
};

export const useDeleteSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, subscriptionId }: { centerId: string; subscriptionId: string; memberId?: string }) =>
            deleteSubscription({ centerId, subscriptionId }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: facilitySubscriptionKeys.all });
            // 라커 관리/구역 현황도 함께 invalidate (lockerNumber 변경 → 라커 관리 페이지 동기화)
            queryClient.invalidateQueries({ queryKey: lockerKeys.all });
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(variables.centerId) });
            if (variables.memberId) {
                queryClient.invalidateQueries({
                    queryKey: facilitySubscriptionKeys.memberList(variables.centerId, variables.memberId),
                });
                queryClient.invalidateQueries({
                    queryKey: publicMemberKeys.detail(variables.centerId, variables.memberId),
                });
            }
        },
    });
};

// 정지 변이 후 공통 invalidate (목록/요약/라커/회원 상세 + 정지 이력)
const invalidateAfterPauseChange = (
    queryClient: ReturnType<typeof useQueryClient>,
    variables: { centerId: string; subscriptionId: string; memberId?: string },
) => {
    queryClient.invalidateQueries({ queryKey: facilitySubscriptionKeys.all });
    queryClient.invalidateQueries({ queryKey: lockerKeys.all });
    queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(variables.centerId) });
    queryClient.invalidateQueries({
        queryKey: facilitySubscriptionKeys.pausePeriods(variables.centerId, variables.subscriptionId),
    });
    if (variables.memberId) {
        queryClient.invalidateQueries({
            queryKey: facilitySubscriptionKeys.memberList(variables.centerId, variables.memberId),
        });
        queryClient.invalidateQueries({
            queryKey: publicMemberKeys.detail(variables.centerId, variables.memberId),
        });
    }
};

export const usePauseSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, subscriptionId, body }: { centerId: string; subscriptionId: string; body: PauseSubscriptionBody; memberId?: string }) =>
            pauseSubscription({ centerId, subscriptionId, body }),
        onSuccess: (_, variables) => invalidateAfterPauseChange(queryClient, variables),
    });
};

export const useResumeSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, subscriptionId, body }: { centerId: string; subscriptionId: string; body: ResumeSubscriptionBody; memberId?: string }) =>
            resumeSubscription({ centerId, subscriptionId, body }),
        onSuccess: (_, variables) => invalidateAfterPauseChange(queryClient, variables),
    });
};

export const useCancelPause = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, subscriptionId, periodId, body }: { centerId: string; subscriptionId: string; periodId: string; body?: CancelPauseBody; memberId?: string }) =>
            cancelPause({ centerId, subscriptionId, periodId, body }),
        onSuccess: (_, variables) => invalidateAfterPauseChange(queryClient, variables),
    });
};

export const useUpdatePausePeriod = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, subscriptionId, periodId, body }: { centerId: string; subscriptionId: string; periodId: string; body: UpdatePausePeriodBody; memberId?: string }) =>
            updatePausePeriod({ centerId, subscriptionId, periodId, body }),
        onSuccess: (_, variables) => invalidateAfterPauseChange(queryClient, variables),
    });
};

export const usePausePeriods = ({ centerId, subscriptionId, enabled = true }: { centerId: string; subscriptionId: string; enabled?: boolean }) =>
    useQuery({
        queryKey: facilitySubscriptionKeys.pausePeriods(centerId, subscriptionId),
        queryFn: () => getPausePeriods({ centerId, subscriptionId }),
        enabled: enabled && !!centerId && !!subscriptionId,
        select: (data) => data?.data,
    });

/**
 * 양도 미리보기. 입력(받는 회원·일수·시작일)이 바뀔 때마다 다시 읽는다 — 호출부가 디바운스한다.
 * 결과에 요청 값(params)을 함께 담아, 화면이 「지금 입력에 대한 미리보기인지」를 확인하게 한다
 * (keepPreviousData 로 이전 입력의 응답이 잠깐 보이는 동안 다음 단계로 넘어가지 않게).
 * 구서버 404 를 재시도하지 않는다.
 */
export const useFacilityTransferPreview = ({
    centerId,
    subscriptionId,
    params,
    enabled = true,
}: {
    centerId: string;
    subscriptionId: string;
    params: FacilityTransferPreviewParams;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...facilitySubscriptionKeys.transferPreview(centerId, subscriptionId), params.toMemberId || '', params.days ?? '', params.startDate || ''],
        queryFn: async () => ({ params, data: await getFacilityTransferPreview({ centerId, subscriptionId, params }) }),
        enabled: enabled && !!centerId && !!subscriptionId,
        keepPreviousData: true,
        // 최근 양도(10분)·남은 일수는 열 때마다 새로 봐야 한다 — 전역 1분 캐시를 쓰지 않는다.
        staleTime: 0,
        cacheTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
    });

export const useFacilityTransfers = ({
    centerId,
    memberId,
    subscriptionId,
    limit = 20,
    enabled = true,
}: {
    centerId: string;
    memberId?: string;
    subscriptionId?: string;
    limit?: number;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...facilitySubscriptionKeys.transfers(centerId), memberId || '', subscriptionId || '', limit],
        queryFn: () => getFacilityTransfers({ centerId, memberId, subscriptionId, limit, page: 1 }),
        enabled: enabled && !!centerId && (!!memberId || !!subscriptionId),
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
    });

/**
 * 양도 실행. 성공·실패 모두 넘겨 준 회원과 받는 회원 양쪽 화면을 새로 읽는다
 * (실패라도 「이용권이 바뀌었어요」면 최신 상태를 보여야 한다).
 */
export const useTransferFacilitySubscription = () => {
    const queryClient = useQueryClient();
    const invalidate = (variables: { centerId: string; fromMemberId?: string; toMemberId?: string }) => {
        queryClient.invalidateQueries({ queryKey: facilitySubscriptionKeys.all });
        queryClient.invalidateQueries({ queryKey: lockerKeys.all });
        queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(variables.centerId) });
        [variables.fromMemberId, variables.toMemberId].forEach((memberId) => {
            if (!memberId) return;
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(variables.centerId, memberId) });
        });
    };
    return useMutation({
        mutationFn: ({
            centerId,
            subscriptionId,
            body,
        }: {
            centerId: string;
            subscriptionId: string;
            body: FacilityTransferBody;
            fromMemberId?: string;
        }) => transferFacilitySubscription({ centerId, subscriptionId, body }),
        onSettled: (_data, _error, variables) =>
            invalidate({ centerId: variables.centerId, fromMemberId: variables.fromMemberId, toMemberId: variables.body.toMemberId }),
    });
};
